// Payer compliance and the compliance log for VITE_CREDENTIALING_MOCKS. The
// result is worked out from the credential mocks on every call, so saving a
// record changes it the way the backend would (WF-D3), in a simplified form.
import {
  ApprovalStatus,
  ComplianceLogEntry,
  CredentialKind,
  CredentialRecord,
  PayerCompliance,
  PersonnelStatus,
  RequirementCompliance,
  TemplateCompliance,
} from '@src/types/personnel';
import { credentialMocks } from './credentialMocks';

type RequirementDef = {
  name: string;
  kind: CredentialKind;
  recordType: string;
  /** a record with no expiration counts for this long (WF-D3) */
  lookback?: string;
  reason: string;
};

type TemplateDef = {
  personnelType: string;
  levelsOfService: Array<string>;
  compliantSince: string;
  requirements: Array<RequirementDef>;
};

const req = (
  name: string,
  kind: CredentialKind,
  recordType: string,
  reason: string,
  lookback?: string
): RequirementDef => ({ kind, lookback, name, reason, recordType });

const AMBULATORY_REQUIREMENTS = [
  req(
    "Driver's License",
    'identification',
    "Driver's License",
    "Driver's License"
  ),
  req(
    'Criminal background check',
    'screening',
    'Background check',
    'Background check',
    '24-month lookback'
  ),
  req('Motor Vehicle Record (MVR)', 'screening', 'MVR', 'MVR'),
  req(
    'Drug screening',
    'screening',
    'Drug test',
    'Drug screening',
    '12-month lookback'
  ),
  req('HIPAA training', 'training', 'HIPAA', 'HIPAA training'),
];

const WHEELCHAIR_REQUIREMENTS = [
  ...AMBULATORY_REQUIREMENTS.slice(0, 4),
  req(
    'OIG/SAM exclusion',
    'screening',
    'OIG/SAM exclusion',
    'OIG/SAM monthly check',
    '30-day lookback'
  ),
  req('PASS training', 'training', 'PASS', 'PASS training'),
  AMBULATORY_REQUIREMENTS[4],
  req('CPR/First Aid', 'training', 'CPR/First Aid', 'CPR/First Aid'),
  req(
    'Wheelchair training',
    'training',
    'Wheelchair training',
    'Wheelchair training',
    '24-month lookback'
  ),
  req(
    'ADA sensitivity training',
    'training',
    'ADA sensitivity training',
    'ADA sensitivity training',
    '24-month lookback'
  ),
  req(
    'Bloodborne pathogens training',
    'training',
    'Bloodborne pathogens',
    'Bloodborne pathogens training',
    '12-month lookback'
  ),
];

const AMBULATORY: TemplateDef = {
  compliantSince: '03/02/2026',
  levelsOfService: ['Ambulatory curb to curb', 'Ambulatory door to door'],
  personnelType: 'Ambulatory Driver',
  requirements: AMBULATORY_REQUIREMENTS,
};

const WHEELCHAIR: TemplateDef = {
  compliantSince: '03/09/2026',
  levelsOfService: ['Wheelchair'],
  personnelType: 'Wheelchair Driver',
  requirements: WHEELCHAIR_REQUIREMENTS,
};

const GURNEY: TemplateDef = {
  compliantSince: '04/02/2026',
  levelsOfService: ['Gurney'],
  personnelType: 'Gurney Driver',
  requirements: AMBULATORY_REQUIREMENTS,
};

const PAYERS: Array<{
  id: number;
  name: string;
  templates: Array<TemplateDef>;
}> = [
  {
    id: 1,
    name: 'IEHP Health Plan',
    templates: [AMBULATORY, WHEELCHAIR, GURNEY],
  },
  { id: 2, name: 'LA Care Health Plan', templates: [AMBULATORY, WHEELCHAIR] },
  { id: 3, name: 'Molina Healthcare', templates: [AMBULATORY] },
];

const COUNTING: Array<ApprovalStatus> = ['Approved', 'Approved automatically'];

const allRecords = (personnelId: number): Array<CredentialRecord> =>
  (['identification', 'screening', 'training', 'permit'] as const).flatMap(
    (kind) => credentialMocks.list({ kind, personnelId })
  );

const withLookback = (text: string, lookback?: string) =>
  lookback ? `${text} · ${lookback}` : text;

const evaluate = (
  def: RequirementDef,
  records: Array<CredentialRecord>,
  payerId: number,
  id: number
): RequirementCompliance & { reasonText: string | null } => {
  // the newest record of the type: the mocks keep the newest first
  const record = records.find(
    ({ kind, type }) => kind === def.kind && type === def.recordType
  );

  if (!record) {
    return {
      approval: 'Not added',
      details: withLookback('No record yet', def.lookback),
      id,
      kind: def.kind,
      name: def.name,
      reasonText: `${def.reason} missing`,
      recordEditable: false,
      recordId: null,
      recordType: def.recordType,
      status: 'Non-Compliant',
      validity: null,
    };
  }

  const approval =
    record.approvals.find((item) => item.payerId === payerId)?.status ??
    'Not sent yet';
  const overdueCheck = record.monthlyChecks?.find(
    ({ validity }) => validity === 'Overdue'
  );
  const validity = overdueCheck ? 'Overdue' : record.validity;
  const counts =
    COUNTING.includes(approval) && validity !== 'Expired' && !overdueCheck;
  const { completionDate, expirationDate, issueDate } = record.values;

  let details = expirationDate
    ? `Expires ${expirationDate}`
    : withLookback(`Completed ${completionDate ?? issueDate}`, def.lookback);
  let reasonText: string | null = null;

  if (overdueCheck) {
    details = withLookback(
      `Monthly check due ${overdueCheck.checkDate} not run`,
      def.lookback
    );
    reasonText = `${def.reason} overdue`;
  } else if (validity === 'Expired') {
    details = `Expired ${expirationDate}`;
    reasonText = `${def.reason} expired`;
  } else if (approval === 'Rejected') {
    const rejection = record.approvals.find(
      (item) => item.payerId === payerId
    )?.rejectionReason;
    details = withLookback(rejection ?? 'Rejected', def.lookback);
    reasonText = `${def.reason} rejected`;
  } else if (!counts) {
    details = withLookback(`Sent ${completionDate ?? issueDate}`, def.lookback);
    reasonText = `${def.reason} pending review`;
  }

  return {
    approval,
    details,
    id,
    kind: def.kind,
    name: def.name,
    reasonText,
    recordEditable: !record.approvals.some(({ status }) =>
      ['Approved', 'Approved automatically', 'In Review'].includes(status)
    ),
    recordId: record.id,
    recordType: def.recordType,
    status: counts ? 'Compliant' : 'Non-Compliant',
    validity,
  };
};

// prettier-ignore
let complianceLog: Record<number, Array<ComplianceLogEntry>> = {
  5: [
    { id: 5, payerName: 'LA Care Health Plan', startDate: '04/02/2026', endDate: '11/02/2026', isProjected: true, entryType: 'Compliancy' },
    { id: 4, payerName: 'Molina Healthcare', startDate: '03/09/2026', endDate: '11/02/2026', isProjected: true, entryType: 'Compliancy' },
    { id: 3, payerName: 'LA Care Health Plan', startDate: '03/04/2026', endDate: '03/10/2026', isProjected: false, entryType: 'Compliancy' },
    { id: 2, payerName: 'IEHP Health Plan', startDate: '03/02/2026', endDate: '12/05/2026', isProjected: true, entryType: 'Compliancy' },
    { id: 1, payerName: 'All Linked Payers', startDate: '03/01/2026', endDate: null, isProjected: false, entryType: 'Onboarded' },
  ],
};

let nextLogId = 100;

const todayText = () => {
  const today = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(today.getMonth() + 1)}/${pad(today.getDate())}/${today.getFullYear()}`;
};

const ALL_PAYERS = 'All Linked Payers';
const OFF: Array<PersonnelStatus> = ['Inactive', 'Terminated'];

/**
 * WF-D4 card 3: Inactive / Terminated is an offboarding, Active again after
 * one is an onboarding; Suspended is neither, and payer rows stay open.
 */
export const recordStatusChange = (
  personnelId: number,
  from: PersonnelStatus,
  to: PersonnelStatus
) => {
  const entries = complianceLog[personnelId] ?? [];
  const lastOnboarding = entries.find(
    ({ entryType }) => entryType === 'Onboarded'
  );
  let entry: ComplianceLogEntry | null = null;

  if (OFF.includes(to) && !OFF.includes(from)) {
    entry = {
      endDate: todayText(),
      entryType: 'Offboarded',
      id: nextLogId++,
      isProjected: false,
      payerName: ALL_PAYERS,
      startDate: lastOnboarding?.startDate ?? todayText(),
    };
  } else if (to === 'Active' && OFF.includes(from)) {
    entry = {
      endDate: null,
      entryType: 'Onboarded',
      id: nextLogId++,
      isProjected: false,
      payerName: ALL_PAYERS,
      startDate: todayText(),
    };
  }

  if (entry)
    complianceLog = { ...complianceLog, [personnelId]: [entry, ...entries] };
};

export const complianceMocks = {
  payerCompliance: ({ personnelId }: { personnelId: number }) => {
    const records = allRecords(personnelId);
    let nextId = 1;

    return PAYERS.map(
      ({ id: payerId, name, templates }): PayerCompliance => ({
        payerId,
        payerName: name,
        templates: templates.map((def, index): TemplateCompliance => {
          const evaluated = def.requirements.map((item) =>
            evaluate(item, records, payerId, nextId++)
          );
          const reasons = evaluated
            .map(({ reasonText }) => reasonText)
            .filter((text): text is string => !!text);

          return {
            details: reasons.length
              ? reasons.join('; ')
              : `Compliant since ${def.compliantSince}`,
            id: payerId * 10 + index,
            levelsOfService: def.levelsOfService,
            name: `${def.personnelType} · ${name}`,
            personnelType: def.personnelType,
            requirements: evaluated.map(
              ({ reasonText: _reasonText, ...requirement }) => requirement
            ),
            status: reasons.length ? 'Non-Compliant' : 'Compliant',
          };
        }),
      })
    );
  },

  complianceLog: ({ personnelId }: { personnelId: number }) =>
    complianceLog[personnelId] ?? [
      {
        endDate: null,
        entryType: 'Onboarded' as const,
        id: personnelId,
        isProjected: false,
        payerName: ALL_PAYERS,
        startDate: '03/01/2026',
      },
    ],
};
